/**
 * React Query hook for managing Footnote data.
 * This hook provides a clean interface for fetching, caching, and managing footnotes using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { Footnote } from '@services/api/deckManagement/graphql/schema/API';
import { getFootnoteForPage } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';

const useGetFootnotesForPage = (pageId: string | null, options?: StandardQueryOptions) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: createQueryOrMutationKey('getFootnotesForPage', pageId),
    queryFn: async (): Promise<Footnote[]> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Footnotes for the Page using the imported query
      const response = await deckManagementClient.graphql({
        query: getFootnoteForPage,
        variables: {
          page_id: pageId,
        },
      });

      if ('data' in response && response.data?.getFootnoteForPage) {
        return response.data.getFootnoteForPage as Footnote[];
      }

      throw new Error('Failed to fetch footnotes for page or page not found');
    },
    ...createStandardQueryOptions<Footnote[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getFootnoteForPage),
    }),
  });
};

export default useGetFootnotesForPage;
